<template>
  <!-- „Aus der Akte beifügen": Dokumente (Ordnerbaum wie in RA-Micro,
       Rückmeldung 23.10.2026) und – neu (Kundenfeedback 24.10.2026, IV.11) –
       frühere E-Mails der Akte als PDF. Genutzt vom E-Mail-Fenster und vom
       Antwortfeld der Korrespondenz (IV.4). -->
  <v-dialog max-width="640" :model-value="modelValue" persistent @update:model-value="close">
    <v-card class="cap" data-testid="case-attachment-picker" rounded="lg">
      <v-card-title class="d-flex justify-space-between align-center">
        <span>{{ $gt('Aus der Akte beifügen') }}</span>
        <ButtonBase color="grey" icon-style icon-type="cancel" @click="close" />
      </v-card-title>
      <v-card-text class="pa-4 pt-0">
        <!-- Akte -->
        <div ref="caseWrap" class="cap-case">
          <label class="cap-case__label">{{ $gt('Akte') }}</label>
          <div class="cap-case__wrap">
            <button
              v-if="pickerCase"
              class="cap-case__chip"
              :title="$gt('Andere Akte wählen')"
              type="button"
              @click="clearCase"
            >
              <FolderOpen :size="12" />
              <span class="cap-case__num">{{ pickerCase.caseNumber }}</span>
              <span class="cap-case__desc">{{ truncate(pickerCase.caseDescription, 34) }}</span>
              <X class="cap-case__x" :size="13" />
            </button>
            <div v-else class="cap-case__input-wrap">
              <Search class="cap-case__icon" :size="14" />
              <input
                ref="caseInput"
                v-model="caseQuery"
                class="cap-case__input"
                :placeholder="$gt('Fall suchen (Nummer, Beschreibung)…')"
                @focus="showCaseDropdown = true"
              >
            </div>
            <div v-if="showCaseDropdown && !pickerCase" class="cap-case__dropdown">
              <div v-if="casesLoading" class="cap-empty">
                <v-progress-circular color="primary" indeterminate size="20" width="2" />
              </div>
              <template v-else>
                <button
                  v-for="c in filteredCases"
                  :key="c.id"
                  class="cap-case__opt"
                  type="button"
                  @click="selectCase(c)"
                >
                  <FolderOpen :size="13" />
                  <span class="cap-case__opt-text">
                    <span class="cap-case__opt-num">{{ c.caseNumber }}</span>
                    <span class="cap-case__opt-desc">{{ c.caseDescription || '' }}</span>
                  </span>
                </button>
                <div v-if="filteredCases.length === 0" class="cap-empty">{{ $gt('Kein Fall gefunden') }}</div>
              </template>
            </div>
          </div>
        </div>

        <!-- Umschalter Dokumente / E-Mails -->
        <div :aria-label="$gt('Art des Anhangs')" class="cap-tabs" role="tablist">
          <button
            :aria-selected="tab === 'docs' ? 'true' : 'false'"
            class="cap-tab"
            :class="{ 'cap-tab--on': tab === 'docs' }"
            data-testid="cap-tab-docs"
            role="tab"
            type="button"
            @click="tab = 'docs'"
          >
            <FileText :size="14" />{{ $gt('Dokumente') }}
            <span v-if="selectedDocIds.length > 0" class="cap-tab__count">{{ selectedDocIds.length }}</span>
          </button>
          <button
            :aria-selected="tab === 'mails' ? 'true' : 'false'"
            class="cap-tab"
            :class="{ 'cap-tab--on': tab === 'mails' }"
            data-testid="cap-tab-mails"
            role="tab"
            :title="$gt('Frühere E-Mails dieser Akte als PDF anhängen, z. B. bei einer Erinnerung an die Versicherung')"
            type="button"
            @click="openMailsTab"
          >
            <Mail :size="14" />{{ $gt('E-Mails') }}
            <span v-if="selectedMails.length > 0" class="cap-tab__count">{{ selectedMails.length }}</span>
          </button>
        </div>

        <!-- Dokumente: Ordnerbaum -->
        <template v-if="tab === 'docs'">
          <div v-if="pickerCase && groups.length > 1" class="cap-toolbar">
            <span class="cap-toolbar__count">
              {{ documents.length }} {{ documents.length === 1 ? $gt('Dokument') : $gt('Dokumente') }}
              · {{ groups.length }} {{ $gt('Ordner') }}
            </span>
            <button class="cap-toolbar__btn" type="button" @click="setAllFolders(true)">{{ $gt('Alle aufklappen') }}</button>
            <button class="cap-toolbar__btn" type="button" @click="setAllFolders(false)">{{ $gt('Alle zuklappen') }}</button>
          </div>
          <div class="cap-list" data-testid="mail-doc-tree">
            <div v-if="docsLoading" class="cap-empty">
              <v-progress-circular color="primary" indeterminate size="20" width="2" />
            </div>
            <template v-else>
              <template v-for="group in groups" :key="group.key">
                <button
                  v-show="ancestorsOpen(group)"
                  class="cap-folder"
                  :class="{ 'cap-folder--open': isFolderOpen(group.key) }"
                  :data-testid="`mail-doc-folder-${group.key}`"
                  :style="{ '--depth': group.depth }"
                  :title="group.path"
                  type="button"
                  @click="toggleFolder(group.key)"
                >
                  <ChevronDown class="cap-folder__chev" :size="14" />
                  <FolderOpen v-if="isFolderOpen(group.key)" class="cap-folder__icon" :size="15" />
                  <Folder v-else class="cap-folder__icon" :size="15" />
                  <span class="cap-folder__name">{{ $gt(group.label) }}</span>
                  <span class="cap-folder__count" :title="$gt('Dokumente in diesem Ordner und seinen Unterordnern')">{{ group.subtreeCount }}</span>
                </button>
                <template v-if="isFolderOpen(group.key) && ancestorsOpen(group)">
                  <div
                    v-for="doc in group.docs"
                    :key="doc.id"
                    :aria-disabled="doc.locked ? 'true' : undefined"
                    class="cap-item"
                    :class="{ 'cap-item--selected': selectedDocIds.includes(doc.id), 'cap-item--locked': doc.locked }"
                    :style="{ '--depth': group.depth }"
                    :title="doc.locked ? $gt('Gesperrt – wird nicht weitergeleitet') + (doc.lockReason ? ' · ' + doc.lockReason : '') : undefined"
                    @click="toggleDoc(doc)"
                  >
                    <Lock v-if="doc.locked" class="cap-item__icon cap-item__icon--locked" :size="16" />
                    <FileText v-else class="cap-item__icon" :size="16" />
                    <div class="cap-item__info">
                      <span class="cap-item__name">{{ doc.title || doc.filename }}</span>
                      <span class="cap-item__meta">
                        <template v-if="doc.locked">{{ $gt('Gesperrt – nicht weiterleitbar') }}</template>
                        <template v-else>{{ formatFileSize(doc.fileSize) }}<template v-if="doc.createdAt"> · {{ formatDate(doc.createdAt) }}</template></template>
                      </span>
                    </div>
                    <Check v-if="selectedDocIds.includes(doc.id)" class="cap-item__check" :size="16" />
                  </div>
                </template>
              </template>
              <div v-if="!pickerCase" class="cap-empty">{{ $gt('Bitte zuerst eine Akte auswählen') }}</div>
              <div v-else-if="documents.length === 0" class="cap-empty">{{ $gt('Keine Dokumente vorhanden') }}</div>
            </template>
          </div>
        </template>

        <!-- E-Mails der Akte (IV.11) -->
        <template v-else>
          <p class="cap-hint">{{ $gt('Die gewählten E-Mails werden als PDF angehängt (mit Absender, Empfänger, Datum und Betreff).') }}</p>
          <div class="cap-list" data-testid="mail-mail-list">
            <div v-if="mailsLoading" class="cap-empty">
              <v-progress-circular color="primary" indeterminate size="20" width="2" />
            </div>
            <template v-else>
              <template v-for="conv in conversations" :key="conv.id">
                <button
                  class="cap-folder"
                  :class="{ 'cap-folder--open': expandedConv[conv.id] }"
                  :data-testid="`mail-conv-${conv.id}`"
                  type="button"
                  @click="toggleConversation(conv)"
                >
                  <ChevronDown class="cap-folder__chev" :size="14" />
                  <MessagesSquare class="cap-folder__icon cap-folder__icon--mail" :size="15" />
                  <span class="cap-folder__name">
                    {{ conv.subject || $gt('(Kein Betreff)') }}
                    <span v-if="counterpartsOf(conv)" class="cap-folder__sub">· {{ counterpartsOf(conv) }}</span>
                  </span>
                  <span class="cap-folder__date">{{ formatMailDate(activityOf(conv)) }}</span>
                </button>
                <template v-if="expandedConv[conv.id]">
                  <div v-if="messagesLoading[conv.id]" class="cap-empty cap-empty--sm">
                    <v-progress-circular color="primary" indeterminate size="16" width="2" />
                  </div>
                  <div
                    v-for="entry in entriesOf(conv)"
                    :key="entry.id"
                    class="cap-item"
                    :class="{ 'cap-item--selected': isMailSelected(entry.id) }"
                    :data-testid="`mail-msg-${entry.id}`"
                    :style="{ '--depth': 0 }"
                    @click="toggleMail(conv, entry)"
                  >
                    <ArrowUpRight v-if="entry.outgoing" class="cap-item__icon" :size="16" :title="$gt('Gesendet')" />
                    <ArrowDownLeft v-else class="cap-item__icon cap-item__icon--in" :size="16" :title="$gt('Eingegangen')" />
                    <div class="cap-item__info">
                      <span class="cap-item__name">
                        {{ entry.outgoing ? $gt('Gesendet') : $gt('Eingegangen') }} · {{ entry.sender }}
                        <template v-if="entry.attachmentCount"> · <Paperclip :size="11" /> {{ entry.attachmentCount }}</template>
                      </span>
                      <span class="cap-item__meta">{{ formatMailDate(entry.date) }}<template v-if="entry.snippet"> · {{ entry.snippet }}</template></span>
                    </div>
                    <Check v-if="isMailSelected(entry.id)" class="cap-item__check" :size="16" />
                  </div>
                </template>
              </template>
              <div v-if="!pickerCase" class="cap-empty">{{ $gt('Bitte zuerst eine Akte auswählen') }}</div>
              <div v-else-if="conversations.length === 0" class="cap-empty">{{ $gt('Keine E-Mails in dieser Akte') }}</div>
            </template>
          </div>
        </template>
      </v-card-text>
      <v-divider />
      <v-card-actions class="d-flex justify-end ma-2">
        <ButtonBase button-text="Abbrechen" icon-type="cancel" @click="close" />
        <ButtonBase
          button-text="Anhängen"
          :disabled="totalSelected === 0 || exporting"
          icon-type="clip"
          :loading="exporting"
          variant="flat"
          @click="attach"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
  import type { ChatConversation, ChatMessage } from '../chat/chatModel'
  import type { MailEntry } from './mailAttachmentPicker'
  import type { PickerFolder } from '@/utils/mailDocPicker'
  import {
    ArrowDownLeft, ArrowUpRight, Check, ChevronDown, FileText, Folder, FolderOpen, Lock, Mail,
    MessagesSquare, Paperclip, Search, X,
  } from 'lucide-vue-next'
  import { computed, nextTick, onBeforeUnmount, reactive, ref, watch } from 'vue'
  import ButtonBase from '@/components/buttons/ButtonBase.vue'
  import { API_BASE_URL } from '@/config'
  import { gt } from '@/i18n/gt'
  import { useAuthStore } from '@/stores/authStore'
  import { useSnackbarStore } from '@/stores/snackbarStore'
  import { groupDocumentsByFolder } from '@/utils/mailDocPicker'
  import { exportMessagePdf } from '../chat/chatApi'
  import { conversationActivity } from '../chat/chatModel'
  import {
    conversationCounterparts,
    formatMailDate,
    mailEntries,
    sortConversationsByActivity,
    uniqueFileNames,
  } from './mailAttachmentPicker'

  export interface PickerCase {
    id: number | string
    caseNumber?: string
    caseDescription?: string
  }

  export interface CaseDocument {
    [extra: string]: unknown
    id: number
    category: string
    title?: string
    filename?: string
    fileSize?: number
    createdAt?: string
    locked?: boolean
    lockReason?: string
  }

  export interface AttachPayload {
    documents: Array<{ doc: CaseDocument, idCase: number, caseNumber: string }>
    files: File[]
  }

  const props = withDefaults(defineProps<{
    modelValue: boolean
    /** Vorausgewählte Akte (Fall des E-Mail-Fensters bzw. der Unterhaltung). */
    initialCase?: PickerCase | null
    /** Bereits geladene Fallliste; ohne Angabe lädt der Dialog sie selbst. */
    cases?: PickerCase[] | null
    /** Beim Öffnen direkt die E-Mails zeigen. */
    initialTab?: 'docs' | 'mails'
  }>(), { initialCase: null, cases: null, initialTab: 'docs' })

  const emit = defineEmits<{
    'update:modelValue': [open: boolean]
    'attach': [payload: AttachPayload]
  }>()

  const snackbar = useSnackbarStore()
  const auth = useAuthStore()
  const viewer = computed(() => ({ idUser: Number(auth.idUser) || 0, idOrganization: Number(auth.idOrganization) || 0 }))

  // ── Akte ──────────────────────────────────────────────────────────
  const ownCases = ref<PickerCase[]>([])
  const casesLoading = ref(false)
  const pickerCase = ref<PickerCase | null>(null)
  const caseQuery = ref('')
  const showCaseDropdown = ref(false)
  const caseWrap = ref<HTMLElement | null>(null)
  const caseInput = ref<HTMLInputElement | null>(null)
  const tab = ref<'docs' | 'mails'>('docs')

  const allCases = computed(() => (props.cases && props.cases.length > 0 ? props.cases : ownCases.value))
  const filteredCases = computed(() => {
    const q = caseQuery.value.trim().toLowerCase()
    const list = allCases.value
    if (!q) return list.slice(0, 50)
    return list.filter(c => String(c.caseNumber || '').toLowerCase().includes(q)
      || String(c.caseDescription || '').toLowerCase().includes(q)).slice(0, 50)
  })

  async function loadCases () {
    if ((props.cases && props.cases.length > 0) || ownCases.value.length > 0) return
    casesLoading.value = true
    try {
      const data = await fetchJson<unknown>(`/protected/cases`)
      const list = Array.isArray(data) ? data : ((data as { cases?: unknown[] } | null)?.cases || [])
      ownCases.value = (list as Array<Record<string, unknown>>).map(c => ({
        id: Number(c.id ?? c.idCase),
        caseNumber: String(c.caseNumber || c.number || ''),
        caseDescription: String(c.caseDescription || c.description || c.title || ''),
      })).filter(c => c.id)
    } catch {
      snackbar.showError(gt('Fälle konnten nicht geladen werden'))
    } finally {
      casesLoading.value = false
    }
  }

  async function completeInitialCase (idCase: number) {
    await loadCases()
    const match = allCases.value.find(c => Number(c.id) === idCase)
    if (match && Number(pickerCase.value?.id) === idCase) pickerCase.value = { ...pickerCase.value, ...match }
  }

  async function fetchJson<T> (path: string): Promise<T | null> {
    const res = await fetch(`${API_BASE_URL}${path}`, { credentials: 'include' })
    if (!res.ok) return null
    return res.json() as Promise<T>
  }

  function selectCase (c: PickerCase) {
    pickerCase.value = c
    caseQuery.value = ''
    showCaseDropdown.value = false
    resetSelection()
    void loadForCase()
  }

  function clearCase () {
    pickerCase.value = null
    documents.value = []
    conversations.value = []
    resetSelection()
    showCaseDropdown.value = true
    void loadCases()
    nextTick(() => caseInput.value?.focus())
  }

  function resetSelection () {
    selectedDocIds.value = []
    selectedMails.value = []
  }

  // ── Dokumente ─────────────────────────────────────────────────────
  const documents = ref<CaseDocument[]>([])
  const folders = ref<PickerFolder[]>([])
  const docsLoading = ref(false)
  const closedFolders = ref<Record<string, boolean>>({})
  const selectedDocIds = ref<number[]>([])
  const groups = computed(() => groupDocumentsByFolder(documents.value, folders.value))

  async function loadDocuments (idCase: number) {
    docsLoading.value = true
    closedFolders.value = {}
    try {
      const [docs, targets] = await Promise.all([
        fetchJson<CaseDocument[]>(`/protected/cases/${idCase}/documents`).catch(() => null),
        fetchJson<{ folders?: Array<Record<string, unknown>> }>(`/protected/cases/${idCase}/doc-folders/targets`).catch(() => null),
      ])
      if (Number(pickerCase.value?.id) !== idCase) return
      documents.value = Array.isArray(docs) ? docs : []
      folders.value = (Array.isArray(targets?.folders) ? targets.folders : [])
        .filter(f => f && typeof f.key === 'string')
        .map(f => ({
          key: String(f.key),
          parentKey: (f.parentKey as string | null) ?? null,
          label: String(f.label || f.key),
          path: String(f.path || f.label || f.key),
        }))
    } finally {
      docsLoading.value = false
    }
  }

  function isFolderOpen (key: string) {
    return !closedFolders.value[key]
  }
  function ancestorsOpen (group: { ancestorKeys?: string[] }) {
    return (group.ancestorKeys || []).every(k => isFolderOpen(k))
  }
  function toggleFolder (key: string) {
    closedFolders.value = { ...closedFolders.value, [key]: !closedFolders.value[key] }
  }
  function setAllFolders (open: boolean) {
    closedFolders.value = open ? {} : Object.fromEntries(groups.value.map(g => [g.key, true]))
  }
  function toggleDoc (doc: CaseDocument) {
    if (doc.locked) return
    selectedDocIds.value = selectedDocIds.value.includes(doc.id)
      ? selectedDocIds.value.filter(id => id !== doc.id)
      : [...selectedDocIds.value, doc.id]
  }

  // ── E-Mails (IV.11) ───────────────────────────────────────────────
  const conversations = ref<ChatConversation[]>([])
  const mailsLoading = ref(false)
  const mailsLoadedFor = ref<number | null>(null)
  const expandedConv = reactive<Record<number, boolean>>({})
  const messagesLoading = reactive<Record<number, boolean>>({})
  const messagesByConv = reactive<Record<number, ChatMessage[]>>({})
  const selectedMails = ref<Array<{ id: number, subject: string }>>([])
  const exporting = ref(false)

  async function loadConversations (idCase: number) {
    if (mailsLoadedFor.value === idCase) return
    mailsLoading.value = true
    try {
      const list = await fetchJson<ChatConversation[]>(`/protected/cases/${idCase}/conversations`).catch(() => null)
      if (Number(pickerCase.value?.id) !== idCase) return
      conversations.value = sortConversationsByActivity((Array.isArray(list) ? list : []).filter(c => c.lastMessage))
      mailsLoadedFor.value = idCase
      for (const key of Object.keys(expandedConv)) delete expandedConv[Number(key)]
      // Eine einzige Unterhaltung gleich aufklappen.
      const only = conversations.value.length === 1 ? conversations.value[0] : null
      if (only) void toggleConversation(only)
    } finally {
      mailsLoading.value = false
    }
  }

  function openMailsTab () {
    tab.value = 'mails'
    if (pickerCase.value) void loadConversations(Number(pickerCase.value.id))
  }

  async function toggleConversation (conv: ChatConversation) {
    expandedConv[conv.id] = !expandedConv[conv.id]
    if (!expandedConv[conv.id] || messagesByConv[conv.id] || !pickerCase.value) return
    messagesLoading[conv.id] = true
    try {
      // markRead=0: Auswahl ist kein Öffnen – Lesestand bleibt unverändert.
      const list = await fetchJson<ChatMessage[]>(
        `/protected/cases/${pickerCase.value.id}/conversations/${conv.id}/messages?markRead=0`,
      ).catch(() => null)
      messagesByConv[conv.id] = Array.isArray(list) ? list : []
    } finally {
      messagesLoading[conv.id] = false
    }
  }

  function entriesOf (conv: ChatConversation): MailEntry[] {
    return mailEntries(messagesByConv[conv.id] || [], viewer.value)
  }
  function counterpartsOf (conv: ChatConversation) {
    return conversationCounterparts(conv, viewer.value)
  }
  function activityOf (conv: ChatConversation) {
    return conversationActivity(conv)
  }
  function isMailSelected (id: number) {
    return selectedMails.value.some(m => m.id === id)
  }
  function toggleMail (conv: ChatConversation, entry: MailEntry) {
    selectedMails.value = isMailSelected(entry.id)
      ? selectedMails.value.filter(m => m.id !== entry.id)
      : [...selectedMails.value, { id: entry.id, subject: conv.subject || '' }]
  }

  // ── Übernehmen ────────────────────────────────────────────────────
  const totalSelected = computed(() => selectedDocIds.value.length + selectedMails.value.length)

  async function exportMails (): Promise<File[]> {
    const files: File[] = []
    const failed: string[] = []
    for (const mail of selectedMails.value) {
      try {
        files.push(await exportMessagePdf(mail.id))
      } catch {
        failed.push(mail.subject || gt('E-Mail'))
      }
    }
    if (failed.length > 0) snackbar.showError(`${gt('Nicht angehängt')}: ${failed.join(', ')}`)
    const names = uniqueFileNames(files.map(f => f.name))
    return files.map((f, i) => {
      const name = names[i] || f.name
      return name === f.name ? f : new File([f], name, { type: f.type })
    })
  }

  async function attach () {
    if (!pickerCase.value || totalSelected.value === 0 || exporting.value) return
    const idCase = Number(pickerCase.value.id)
    const caseNumber = pickerCase.value.caseNumber || ''
    const docs: AttachPayload['documents'] = []
    for (const id of selectedDocIds.value) {
      const doc = documents.value.find(d => d.id === id)
      if (doc) {
        docs.push({ doc, idCase, caseNumber })
      }
    }
    exporting.value = true
    try {
      const files = selectedMails.value.length > 0 ? await exportMails() : []
      emit('attach', { documents: docs, files })
      close()
    } finally {
      exporting.value = false
    }
  }

  function close () {
    showCaseDropdown.value = false
    emit('update:modelValue', false)
  }

  async function loadForCase () {
    if (!pickerCase.value) return
    const id = Number(pickerCase.value.id)
    mailsLoadedFor.value = null
    for (const key of Object.keys(messagesByConv)) delete messagesByConv[Number(key)]
    conversations.value = []
    await Promise.all([
      loadDocuments(id),
      tab.value === 'mails' ? loadConversations(id) : Promise.resolve(),
    ])
  }

  // Beim Öffnen: Akte des Fensters vorwählen, sonst Aktensuche anbieten.
  watch(() => props.modelValue, open => {
    if (!open) return
    tab.value = props.initialTab
    resetSelection()
    caseQuery.value = ''
    const initial = props.initialCase && props.initialCase.id ? props.initialCase : null
    const changed = Number(initial?.id || 0) !== Number(pickerCase.value?.id || 0)
    pickerCase.value = initial
    if (!initial) {
      documents.value = []
      conversations.value = []
      showCaseDropdown.value = true
      void loadCases()
      nextTick(() => caseInput.value?.focus())
      return
    }
    showCaseDropdown.value = false
    // Aus dem Antwortfeld kommt nur die Fall-ID – Aktenzeichen und
    // Bezeichnung für das Akten-Kennzeichen nachladen.
    if (!initial.caseNumber) void completeInitialCase(Number(initial.id))
    if (changed || documents.value.length === 0) void loadForCase()
    else if (tab.value === 'mails') void loadConversations(Number(initial.id))
  }, { immediate: true })

  // Klick neben die Aktensuche schließt die Vorschlagsliste.
  function onDocMouseDown (ev: MouseEvent) {
    if (caseWrap.value && !caseWrap.value.contains(ev.target as Node)) showCaseDropdown.value = false
  }
  watch(showCaseDropdown, open => {
    if (open) document.addEventListener('mousedown', onDocMouseDown)
    else document.removeEventListener('mousedown', onDocMouseDown)
  })
  onBeforeUnmount(() => document.removeEventListener('mousedown', onDocMouseDown))

  // ── Formatierung ──────────────────────────────────────────────────
  function truncate (s: string | undefined, n: number) {
    if (!s) return ''
    return s.length > n ? `${s.slice(0, n)}…` : s
  }
  function formatDate (iso: string) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '')
    return m ? `${m[3]}.${m[2]}.${m[1]}` : (iso || '')
  }
  function formatFileSize (bytes?: number) {
    if (!bytes) return '0 B'
    const units = ['B', 'KB', 'MB', 'GB']
    let i = 0
    let size = bytes
    while (size >= 1024 && i < units.length - 1) {
      size /= 1024
      i++
    }
    return `${size.toFixed(i > 0 ? 1 : 0)} ${units[i]}`
  }
</script>

<style scoped>
.cap-case { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
.cap-case__label { flex-shrink: 0; font-size: 0.8rem; font-weight: 600; color: var(--grey-color); }
.cap-case__wrap { position: relative; flex: 1; min-width: 0; display: flex; align-items: center; }
.cap-case__chip {
  display: inline-flex; align-items: center; gap: 6px; max-width: 100%; height: 26px;
  padding: 2px 10px; overflow: hidden;
  border: 1px solid var(--primary-color); border-radius: 999px;
  background: var(--primary-light-color); color: var(--primary-color);
  font-size: 0.82rem; cursor: pointer;
}
.cap-case__num { font-weight: 600; white-space: nowrap; }
.cap-case__desc { color: var(--grey-color); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cap-case__input-wrap { position: relative; display: flex; align-items: center; width: 100%; }
.cap-case__icon { position: absolute; left: 8px; color: var(--grey-color); pointer-events: none; }
.cap-case__input {
  flex: 1; height: 34px; padding: 4px 10px 4px 28px;
  border: 1px solid var(--border-grey-color); border-radius: 8px; outline: none;
  font-size: 0.86rem; font-family: inherit; color: var(--dark-color); background: #fff;
}
.cap-case__input:focus { border-color: var(--primary-color); }
.cap-case__dropdown {
  position: absolute; top: calc(100% + 4px); left: 0; right: 0; z-index: 20;
  max-height: 260px; overflow: auto;
  border: 1px solid var(--border-grey-color); border-radius: 10px;
  background: #fff; box-shadow: 0 10px 30px -12px rgba(15, 23, 42, 0.18);
}
.cap-case__opt {
  display: flex; align-items: center; gap: 10px; width: 100%;
  padding: 9px 12px; border: none; background: none; text-align: left; cursor: pointer;
}
.cap-case__opt:hover { background: var(--grey-light-color); }
.cap-case__opt-text { display: flex; flex-direction: column; min-width: 0; }
.cap-case__opt-num { font-weight: 600; font-size: 0.86rem; color: var(--dark-color); }
.cap-case__opt-desc { font-size: 0.76rem; color: var(--grey-color); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* Umschalter: gewählt blau, sonst weiß (Kundenwunsch: keine grauen Pillen). */
.cap-tabs { display: inline-flex; gap: 6px; margin-bottom: 10px; }
.cap-tab {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 12px; border: 1px solid var(--border-grey-color); border-radius: 999px;
  background: #fff; color: var(--dark-color); font-size: 0.8rem; font-weight: 600; cursor: pointer;
}
.cap-tab:hover { border-color: var(--primary-color); }
.cap-tab:focus-visible { outline: 2px solid var(--primary-color); outline-offset: 2px; }
.cap-tab--on { border-color: var(--primary-color); background: var(--primary-color); color: #fff; }
.cap-tab__count {
  min-width: 18px; padding: 0 5px; border-radius: 999px;
  background: #fff; color: var(--primary-color); font-size: 0.7rem; line-height: 18px; text-align: center;
}
.cap-tab:not(.cap-tab--on) .cap-tab__count { background: var(--primary-color); color: #fff; }

.cap-hint { margin: 0 0 8px; font-size: 0.76rem; color: var(--grey-color); }
.cap-toolbar { display: flex; align-items: center; gap: 10px; margin: 0 0 6px; font-size: 0.74rem; color: var(--grey-color); }
.cap-toolbar__count { flex: 1; min-width: 0; }
.cap-toolbar__btn { padding: 2px 4px; border: 0; background: none; color: var(--primary-color); font-size: 0.74rem; font-weight: 500; cursor: pointer; }
.cap-toolbar__btn:hover { text-decoration: underline; }

.cap-list { max-height: 360px; overflow-y: auto; border: 1px solid var(--border-grey-color); border-radius: 10px; }
.cap-folder {
  position: sticky; top: 0; z-index: 1;
  display: flex; align-items: center; gap: 8px; width: 100%;
  padding: 8px 12px; padding-left: calc(12px + var(--depth, 0) * 18px);
  border: 0; border-top: 1px solid #e6e9ee; background: var(--grey-light-color);
  font: inherit; font-size: 0.8rem; font-weight: 600; color: var(--dark-color); text-align: left; cursor: pointer;
}
.cap-folder:first-child { border-top: 0; }
.cap-folder:hover { background: #e9edf3; }
.cap-folder__chev { flex-shrink: 0; color: var(--grey-color); transform: rotate(-90deg); transition: transform 0.15s; }
.cap-folder--open .cap-folder__chev { transform: rotate(0deg); }
.cap-folder__icon { flex-shrink: 0; color: #c98a00; }
.cap-folder__icon--mail { color: var(--primary-color); }
.cap-folder__name { flex: 1; min-width: 0; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
.cap-folder__sub { font-weight: 400; color: var(--grey-color); }
.cap-folder__date { flex-shrink: 0; font-size: 0.72rem; font-weight: 400; color: var(--grey-color); }
.cap-folder__count {
  padding: 0 7px; border: 1px solid var(--border-grey-color); border-radius: 999px;
  background: #fff; font-size: 0.7rem; font-weight: 600; line-height: 18px; color: var(--grey-color);
}
.cap-item {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 12px; padding-left: calc(30px + var(--depth, 0) * 18px);
  cursor: pointer; transition: background 0.1s;
}
.cap-item + .cap-item { border-top: 1px solid #eef0f3; }
.cap-item:hover { background: var(--grey-light-color); }
.cap-item--selected,
.cap-item--selected:hover { background: var(--primary-light-color); }
.cap-item--locked { opacity: 0.6; cursor: not-allowed; }
.cap-item--locked:hover { background: transparent; }
.cap-item__icon { flex-shrink: 0; color: var(--grey-color); }
.cap-item__icon--in { color: var(--primary-color); }
.cap-item__icon--locked { color: #b91c1c; }
.cap-item__info { flex: 1; min-width: 0; }
.cap-item__name { display: block; overflow: hidden; font-size: 0.85rem; color: var(--dark-color); white-space: nowrap; text-overflow: ellipsis; }
.cap-item__meta { display: block; overflow: hidden; font-size: 0.72rem; color: var(--grey-color); white-space: nowrap; text-overflow: ellipsis; }
.cap-item__check { flex-shrink: 0; color: var(--primary-color); }
.cap-empty { padding: 24px; text-align: center; font-size: 0.85rem; color: var(--grey-color); }
.cap-empty--sm { padding: 10px; }
</style>
